// ContactConnections.tsx — „Verknüpfungen"-Block (read-only, echte Daten) am
// Ende des Allgemein-Tabs. Quelle: contactData.relationsSrc + relationsDest
// (beide Richtungen, da Beziehungen Firma→Person gespeichert werden) und
// contactData.group (Kundenverbindung).
//
// Firma zeigt: Geschäftsführer&Gesellschafter, Verantwortlichkeiten&Zuständig-
//   keiten, Verbindungen zu Firmen, Kundenverbindungen.
// Person zeigt: Haushalt (Kontakt selbst zuerst + same-household-Beziehungen),
//   Weitere Beziehungen, Verbindungen zu Firmen, Kundenverbindungen.
// Kundenverbindungen-Sektion wird immer gerendert (Leerzustand, wenn keine).

import { ohneAnteilsDubletten } from "../../data/anteilsbeziehung";
import * as React from "react";
import { useNavigate } from "react-router";
import { DataTable, type Column, Dash, Chip } from "./table";
import { KontaktmoeglichkeitCell } from "./KontaktmoeglichkeitPopup";
import { RelationCreateDialog, type RelKind, type EditRelation } from "./RelationCreateDialog";
import { MenschenFuersUnternehmenDialog } from "./MenschenFuersUnternehmenDialog";
import { istVereinsRechtsform } from "../../data/vereinsansicht";
import { MenschenImUnternehmenSection } from "./MenschenImUnternehmenSection";
import { OrganigrammPopup } from "./OrganigrammPopup";
import type { VerantwortlichkeitValue, VerantRole } from "../verantwortlichkeit/VerantwortlichkeitInput";
import type { ContactRef } from "../relationsInline/types";
import { apiFetch } from "../../utils/apiFetch";
import { loeschZusatzFuerAbgeleitete } from "../../utils/abgeleiteteBeziehungen";
import { BeziehungsVorschlaege } from "./BeziehungsVorschlaege";
import { reverseKinshipLabel, reverseFirmaLabel } from "../../services/haushalteCleanupService";
import { contactTypeLabel } from "../../utils/contactTypeLabel";
import { genderOf } from "../../utils/householdRole";
import { roleLabelFrom, GFG_COMBINED_LABEL } from "../../utils/derivePersonEmployment";
import { fasseGfGesellschafterZusammen, LABEL_GESELLSCHAFTER } from "../../utils/gfGesellschafter";
import {
  assistenzChefNamen,
  chipsMitAssistenzName,
  type AssistenzPaar,
} from "./assistenzAnzeige";
import {
  useUnternehmensBegriff,
  useUnternehmensBegriffe,
  istKeinUnternehmensBegriff,
  verantwortungTitel,
  diesesUnternehmen,
} from "../../utils/unternehmensBegriff";
import { useVerantwortlichkeiten } from "../verantwortlichkeit/useVerantwortlichkeiten";
import {
  baueKategorieIndex,
  istGesellschafterStrukturKante,
  istStrukturKante,
  sektionFuerZeile,
} from "../verantwortlichkeit/rollenEinordnung";
import {
  buendleFirmenverbindungen,
  firmenverbindungRollenText,
  leitzeileFuerAktion,
  teilzeilenLabel,
} from "../verantwortlichkeit/firmenverbindungenBuendeln";
import type { ContactDetailsFormValues } from "../../types/Contact";
import { elementMitId } from "../windowManager/browserFenster/fensterRegister";

// Zeilen-Aktionen: Pen (Bearbeiten) + Trash (Entfernen) — bewusst größer/klarer.
const DelIcon = (
  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h12M8 6V4.5A1.5 1.5 0 019.5 3h1A1.5 1.5 0 0112 4.5V6M6 6l.7 9a1.5 1.5 0 001.5 1.4h3.6a1.5 1.5 0 001.5-1.4L15 6M8.5 9v4M11.5 9v4" /></svg>
);
const PenIcon = (
  <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M13.5 4.5l2 2M4 16l1-3.5 8-8a1.4 1.4 0 012 2l-8 8L4 16z" /></svg>
);

export type Comm = { type?: string; value?: string; isDefault?: boolean };
export type Party = {
  id?: number;
  displayName?: string;
  lastname?: string;
  firstName?: string;
  typ?: string;
  type?: { type?: string };
  status?: { status?: string } | null;
  communications?: Comm[];
  /** Für die geschlechtsabhängige Gegenrichtung eingehender Beziehungen. */
  gender?: string | null;
  /** Rechtsform des Gegenübers (nur bei Firmen) — eager geladen, s. SelfContact. */
  legalForm?: { id?: number; legalForm?: string } | null;
};
export type RelEntry = {
  /** ContactRelation-Row-id (für gezieltes Bearbeiten/PATCH). */
  id?: number;
  contactDest?: Party;
  contactSrc?: Party;
  relationType?: { contactRelationType?: string };
  reverseRelationType?: string | null;
  function?: string | null;
  share?: string | null;
  isExtern?: boolean;
  sameHousehold?: boolean;
  /** `'derived'` = von KMVS abgeleitet (Plan 2026-09-30, B20). */
  source?: string | null;
};
type Group = {
  id?: number;
  name?: string;
  type?: string;
} | null;
type SelfContact = {
  id?: number;
  name: string;
  typ?: string;
  status?: string;
  gender?: string | null;
  /** Anrede („Herr"/„Frau") — Zweitquelle für das Geschlecht, wenn `gender` fehlt. */
  salutationLabel?: string | null;
  /**
   * Eigene Rechtsform (nur bei Firmen belegt). Sie entscheidet, welche Rollen an
   * dieser Firma vergeben werden dürfen und ob beim Verbinden zuerst eine
   * Rechtsform erfasst werden muss.
   */
  legalForm?: string;
  legalFormId?: number;
};

function nameOf(d: Party): string {
  const composed = `${d.lastname ?? ""}${d.firstName ? ", " + d.firstName : ""}`.replace(/^,\s*|,\s*$/g, "").trim();
  return d.displayName || composed || "—";
}

// Einheitliche Beziehungs-Row aus beiden Richtungen (other = das jeweils andere
// Gegenüber: contactDest bei relationsSrc, contactSrc bei relationsDest).
type Rel = {
  id?: number; // id des ANDEREN Kontakts (Navigation/Delete-between)
  name: string;
  relType: string;
  func?: string;
  share?: string;
  isExtern: boolean;
  sameHousehold: boolean;
  otherType: string; // "Person" | "Firma" | ""
  status?: string;
  isSelf?: boolean;
  /** ContactRelation-Row-id der gespeicherten Zeile (für Bearbeiten/PATCH).
   *  Bei der Self-Zeile UND bei gebündelten Verantwortlichkeiten undefined
   *  (mehrere Rows zu einer Anzeigezeile → nicht eindeutig adressierbar). */
  relationId?: number;
  /** Echte Richtung der gespeicherten Zeile (ContactSrc/ContactDest-Ids). */
  srcId?: number;
  destId?: number;
  /** Geschlecht des Gegenübers (für die Beziehungs-Ableitung im Edit-Dialog). */
  otherGender?: string | null;
  /**
   * Rechtsform des Gegenübers (nur wenn es eine Firma ist). Grundlage für die
   * rechtsformabhängige Rollenauswahl und für die Frage, ob beim Bearbeiten
   * zuerst eine Rechtsform nachgetragen werden muss.
   */
  otherLegalForm?: string;
  otherLegalFormId?: number;
  /** Gespeicherte Freitext-Gegenrichtung (reverseRelationType). */
  reverseRelationType?: string | null;
  /** Rohes gespeichertes Label der Zeile (relationType = „was dest für src ist"). */
  storedRelType?: string;
  /**
   * Richtung der gespeicherten Zeile: true = der aktuelle Kontakt ist contactDest.
   * Entscheidet bei Firma↔Firma-Rollenkanten, wer die Rolle TRÄGT — attach-menschen
   * schreibt immer `src = Unternehmen`, eine eingehende Rollenkante meint also eine
   * Rolle DIESES Kontakts bei der anderen Firma.
   */
  istEingehend?: boolean;
  /** Konsolidierte Verantwortlichkeiten (eine Zeile pro Person). */
  items?: { func: string; isExtern: boolean }[];
  /** Rohe gebündelte Zeilen (für das Edit-Popup: Prefill aller Rollen des Paars). */
  groupRels?: Rel[];
  /** Von KMVS abgeleitet (A ist Bruder von B ⇒ Schwager von Bs Ehefrau) — B20. */
  abgeleitet?: boolean;
};

function mkRel(rel: RelEntry, other: Party | undefined, isInverse = false, selfId?: number): Rel {
  const d = other ?? {};
  const otherType = (d.type?.type || d.typ || "") as string;
  const base = rel.relationType?.contactRelationType || "";
  // Eingehende Beziehung (aktueller Kontakt = dest): das gespeicherte Label gilt aus
  // Sicht von src → für die Anzeige die Gegenrichtung bilden.
  //  - Person↔Person: geschlechtsabhängige Verwandtschafts-Inverse (reverseKinshipLabel).
  //  - Firma↔Firma: geschlechts-unabhängige Firma-Inverse (reverseFirmaLabel), z. B.
  //    Tochterfirma→Mutterfirma; symmetrische (Schwester/Beteiligung) bleiben gleich.
  //  - Unbekannte/Freitext-Labels (Inverse → null) bleiben unverändert.
  //
  // GESPEICHERT SCHLÄGT BERECHNET: `reverseRelationType` beschreibt contactSrc aus
  // Sicht von contactDest — auf einer eingehenden Zeile (other = contactSrc) also
  // exakt das gesuchte Label. Es wird im Dialog ausdrücklich festgelegt, teils vom
  // Nutzer gewählt (wenn selfs Geschlecht fehlt und nichts abgeleitet werden kann).
  // Die Berechnung lief hier vorher ZUERST und lieferte für Katalog-Labels immer
  // einen Wert — die `??`-Kette konnte den gespeicherten Wert damit nie erreichen.
  // Alt-Zeilen ohne gespeicherte Gegenrichtung fallen unverändert auf die
  // Berechnung zurück (`||`, damit auch ein leerer String durchfällt).
  const storedReverse = (rel.reverseRelationType ?? "").trim();
  const relType = !isInverse
    ? base
    : otherType === "Firma"
      ? reverseFirmaLabel(base) ?? base
      : storedReverse || reverseKinshipLabel(base, genderOf(d), { partnerFallback: "opposite" }) || base;
  // Echte Richtung der Zeile: bei relationsSrc ist der aktuelle Kontakt src (other=dest),
  // bei relationsDest ist der aktuelle Kontakt dest (other=src). Der jeweils nicht
  // geladene Endpunkt = aktueller Kontakt (selfId).
  const srcId = isInverse ? d.id : selfId;
  const destId = isInverse ? selfId : d.id;
  return {
    id: d.id,
    name: nameOf(d),
    relType,
    func: rel.function || undefined,
    share: rel.share || undefined,
    isExtern: !!rel.isExtern,
    sameHousehold: !!rel.sameHousehold,
    otherType,
    status: d.status?.status || undefined,
    relationId: rel.id,
    srcId,
    destId,
    otherGender: d.gender ?? null,
    reverseRelationType: rel.reverseRelationType ?? null,
    storedRelType: base,
    istEingehend: isInverse,
    abgeleitet: rel.source === "derived",
    // Rechtsform des Gegenübers durchreichen: In der Person-Sicht ist die
    // Gegen-Party die Firma, deren Rechtsform darüber entscheidet, welche Rollen
    // zulässig sind. `Contact.legalForm` ist eager geladen, lag also schon im
    // Payload und wurde hier nur nicht kopiert.
    otherLegalForm: d.legalForm?.legalForm ?? undefined,
    otherLegalFormId: d.legalForm?.id ?? undefined,
  };
}
function toRels(src: RelEntry[], dest: RelEntry[], selfId?: number): Rel[] {
  return [
    ...(src ?? []).map((r) => mkRel(r, r.contactDest, false, selfId)),
    ...(dest ?? []).map((r) => mkRel(r, r.contactSrc, true, selfId)),
  ];
}

/** Sprungziele der Status-Pille (ContactStatusPill) — als DOM-Anker vergeben. */
export const ANCHOR_HAUSHALT = "rel-haushalt";
export const ANCHOR_VERBINDUNGEN = "rel-verbindungen";

const GF_GESELL = new Set(["Geschäftsführer", "Gesellschafter"]);

/**
 * Beziehungstypen, die `attach-menschen` im Ersetzen-Modus verwaltet: Beim
 * Speichern löscht es ALLE davon zwischen dem Paar und schreibt den Satz aus dem
 * Popup neu (ansprechpartner-cleanup.service.ts). Das Popup muss deshalb ALLE
 * Rollen des Paars kennen, nicht nur die der angeklickten Sektion.
 */
const VERWALTETE_ROLLEN_TYPEN = new Set(["Mensch fürs Unternehmen", "Geschäftsführer", "Gesellschafter"]);

/**
 * Organstellungen, die zwar als „Mensch fürs Unternehmen" verbunden sind, aber
 * zu Geschäftsführung und Gesellschaftern gehören — Vorstand und Prokuristen
 * aus dem Handelsregister landen hier.
 *
 * Der Beziehungstyp allein trägt das nicht: Die konkrete Rolle steht in
 * `function`, weil „Mensch fürs Unternehmen" der Sammeltyp für alles ist, was
 * zur Firma gehört (im Bestand 128 verschiedene Funktionen, von
 * „Ansprechpartner" bis „1. Vorsitzender").
 */
const ORGAN_FUNKTIONEN = new Set([
  "vorstand",
  "prokurist",
  "liquidator",
  "inhaber",
  "aufsichtsratsvorsitzender",
]);
const isOrgan = (r: Rel) =>
  r.relType === "Mensch fürs Unternehmen" &&
  ORGAN_FUNKTIONEN.has((r.func ?? "").trim().toLowerCase());

const isGfG = (r: Rel) => GF_GESELL.has(r.relType) || isOrgan(r);

/**
 * Trägt die (ggf. gebündelte) Zeile eine echte GF-/Gesellschafter-Rolle?
 *
 * Eine Gesellschafter-Strukturkante (Mutter bzw. ausgehende Beteiligung) zählt
 * mit — sie IST eine Beteiligung und gehört nach oben, nicht unter Vorstand und
 * Prokuristen.
 */
const istKernGfG = (r: Rel) =>
  (r.groupRels?.length ? r.groupRels : [r]).some(
    (row) => GF_GESELL.has(row.relType) || istGesellschafterStrukturKante(row),
  );

/**
 * Rolle einer (ggf. gebündelten) GF/Gesellschafter-Zeile.
 *
 * Derselbe Sachverhalt liegt in ZWEI Datenrepräsentationen vor:
 *  a) eine Zeile `Gesellschafter` + function='geschäftsführend' (RelationCreateDialog)
 *  b) zwei Zeilen `Geschäftsführer` + `Gesellschafter` (VerantwortlichkeitInput)
 * Beide ergeben hier dasselbe Label, damit eine Person nicht doppelt erscheint.
 */
function gfgRolle(r: Rel): string {
  const rows = r.groupRels?.length ? r.groupRels : [r];
  return fasseGfGesellschafterZusammen(
    rows.map((row) =>
      // Eine Struktur-Kante trägt ihren Sachverhalt im Typ („Mutterfirma",
      // „Beteiligung"). In DIESER Tabelle heißt der Sachverhalt aber
      // „Gesellschafter" — und nur unter diesem Namen fasst
      // fasseGfGesellschafterZusammen ihn mit einer Geschäftsführung zur
      // Personalunion zusammen.
      istGesellschafterStrukturKante(row)
        ? LABEL_GESELLSCHAFTER
        : roleLabelFrom(row.relType, row.func),
    ),
  ).join(", ");
}

/**
 * Anteile einer gebündelten Zeile.
 *
 * Bis 08/2026 gewann hier der erste gesetzte Wert — solange nur „Gesellschafter"
 * eine Quote tragen konnte, gab es je Paar auch nur eine. Seit die Quote an jeder
 * Rolle hängt, die laut Matrix eine trägt (Komplementärin in Prozent UND
 * Kommanditist in Euro am selben Paar), muss jede mit ihrer Rolle dastehen —
 * sonst verschwände die zweite stillschweigend.
 *
 * Seit „Verbindungen zu Firmen" je Gegenüber bündelt, kann eine Zeile auch
 * gegenläufige Kanten tragen. Das Rollen-Präfix nennt deshalb die Richtung mit
 * (`teilzeilenLabel`) — ohne sie stünde bei zwei Quoten zweimal dasselbe Wort.
 */
function AnteileZelle({ rel }: { rel: Rel }) {
  const rows = (rel.groupRels?.length ? rel.groupRels : [rel]).filter((r) => (r.share ?? "").trim());
  if (!rows.length) return <Dash />;
  if (rows.length === 1) return <>{rows[0].share}</>;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", rowGap: 2 }}>
      {rows.map((r, i) => (
        <span key={i}>
          {teilzeilenLabel(r)}: {r.share}
        </span>
      ))}
    </span>
  );
}
/**
 * Verantwortlichkeiten je Person zusammenfassen: pro Person genau eine Zeile,
 * alle Verantwortlichkeiten (function/Rolle) als `items` gesammelt. Die
 * repräsentativen Stammfelder (id/name/Typ/Status) stammen vom ersten Eintrag.
 */
function groupVerant(rels: Rel[]): Rel[] {
  const map = new Map<string, Rel>();
  const order: string[] = [];
  for (const r of rels) {
    const key = r.id != null ? "id:" + r.id : "nm:" + r.name;
    let row = map.get(key);
    if (!row) {
      // Gebündelte Anzeige (mehrere Rows je Person) → keine eindeutige relationId/
      // Richtung. Bearbeitet wird über das Verantwortlichkeit-Popup (alle Rollen des
      // Paars, groupRels). `items` bleibt für die Chip-Anzeige.
      row = { ...r, relationId: undefined, srcId: undefined, destId: undefined, items: [], groupRels: [] };
      map.set(key, row);
      order.push(key);
    }
    row.groupRels!.push(r);
    // Sprechendes Label statt des Rohwerts: bei „Gesellschafter" +
    // function='geschäftsführend' stand hier sonst der interne Flag-Wert
    // „geschäftsführend" im Chip.
    const func = roleLabelFrom(r.relType, r.func) || r.func || r.relType || "";
    if (func && !row.items!.some((it) => it.func === func)) {
      row.items!.push({ func, isExtern: r.isExtern });
    }
  }
  return order.map((k) => map.get(k)!);
}

/** Rohe gebündelte Rel-Zeilen → VerantwortlichkeitValue (Prefill fürs Edit-Popup). */
function relsToVerant(rels?: Rel[]): VerantwortlichkeitValue {
  const roles: VerantRole[] = [];
  let isExtern = false;
  for (const r of rels ?? []) {
    if (r.relType === "Geschäftsführer") {
      roles.push({ kind: "gf" });
    } else if (r.relType === "Gesellschafter") {
      roles.push({ kind: "gesellschafter", share: r.share ?? "", istGeschaeftsfuehrer: r.func === "geschäftsführend" });
    } else {
      // Die Quote gehoert zur Rolle, nicht nur zum Gesellschafter: Ohne sie
      // schriebe das Popup beim naechsten Speichern eine leere `share` zurueck
      // (attach-menschen ersetzt alle verwalteten Zeilen des Paars).
      roles.push({ kind: "mensch", verantwortlichkeit: r.func || r.relType || "", share: r.share ?? undefined });
      if (r.isExtern) isExtern = true;
    }
  }
  return { isExtern, roles, externalCompany: null };
}

const InfoIcon = (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6.5" /><path d="M8 7.3v3.4M8 5.2h.01" /></svg>
);

/** Verantwortlichkeiten einer Person als Chips (mit intern/extern); Overflow → (i)-Tooltip. */
function VerantList({ items }: { items?: { func: string; isExtern: boolean }[] }) {
  const list = (items ?? []).filter((it) => it.func);
  if (list.length === 0) return <Dash />;
  const MAX = 2;
  const shown = list.slice(0, MAX);
  const restCount = list.length - shown.length;
  const fullText = list.map((it) => `${it.func} (${it.isExtern ? "extern" : "intern"})`).join("\n");
  return (
    <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 5 }}>
      {shown.map((it, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 9px", borderRadius: 999, background: "#f3eef6" }}>
          <span style={{ font: "600 12.5px/1.1 'Hanken Grotesk', sans-serif", color: "#241522" }}>{it.func}</span>
          <span style={{ font: "700 9.5px/1 'Hanken Grotesk', sans-serif", textTransform: "uppercase", letterSpacing: ".04em", color: it.isExtern ? "#b0492f" : "#2f6db0" }}>
            {it.isExtern ? "extern" : "intern"}
          </span>
        </span>
      ))}
      {restCount > 0 && (
        <span
          title={fullText}
          tabIndex={0}
          aria-label={`${restCount} weitere: ${fullText.replace(/\n/g, "; ")}`}
          style={{ display: "inline-flex", alignItems: "center", gap: 3, cursor: "help", color: "#7d2169", font: "700 12px/1 'Hanken Grotesk', sans-serif" }}
        >
          +{restCount}
          {InfoIcon}
        </span>
      )}
    </span>
  );
}

function StatusChip({ s }: { s?: string }) {
  if (!s) return <Dash />;
  const tone = s === "Kunde" ? "chip-kunde" : s === "Interessent" ? "chip-interessent" : "chip-sonstige";
  return <Chip tone={tone}>{s}</Chip>;
}
/** Kontaktmöglichkeit wie in der Kontakttabelle: Telefon/Mail-Icons → Popup. */
function KomCell({ rel }: { rel: Rel }) {
  if (!rel.id) return <Dash />;
  const row = { kontaktId: rel.id, typ: rel.otherType, displayName: rel.name } as unknown as ContactDetailsFormValues;
  return <KontaktmoeglichkeitCell row={row} />;
}

/** Kundenverbindungs-Box: Name + Mitgliederzahl, ausklappbar → Mitglieder (lazy). */
function KundenverbindungBox({ group }: { group: { id: number; name: string } }) {
  const [open, setOpen] = React.useState(false);
  const [members, setMembers] = React.useState<{ id: number; name: string; typ: string }[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    apiFetch<{ id: number; name: string; typ: string }[]>(`contactgroup/${group.id}/contacts`)
      .then(({ data }) => { if (!cancelled) setMembers(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setMembers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [group.id]);
  const count = members?.length ?? 0;
  const chev = (
    <svg className="chev" width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M6 3.5L10.5 8 6 12.5" /></svg>
  );
  return (
    <div className="kv-list">
      <div className={"kv-grp" + (open ? " open" : "")}>
        <div className="kv-grp-head" onClick={() => setOpen((o) => !o)}>
          {chev}
          <span className="kv-grp-name">{group.name}</span>
          <span className="kv-grp-count">{loading ? "(…)" : `(${count} Kontakt${count === 1 ? "" : "e"})`}</span>
        </div>
        <div className="kv-members">
          {loading ? (
            <div className="kv-member" style={{ color: "var(--ink-3)" }}>Lädt …</div>
          ) : count > 0 ? (
            (members ?? []).map((m) => <div key={m.id} className="kv-member">{m.name}</div>)
          ) : (
            <div className="kv-member" style={{ color: "var(--ink-3)" }}>Keine Kontakte in dieser Kundenverbindung.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Benannte Gruppe (Überschrift + Untertitel) über mehreren RelSections.
function RelGroup({ groupTitle, groupSub, children }: { groupTitle: string; groupSub: string; children: React.ReactNode }) {
  return (
    <div className="relgroup" style={{ marginBottom: 32 }}>
      <div className="relgroup-h" style={{ marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid var(--line, #ddd3dc)" }}>
        <h3 style={{ margin: "0 0 3px", font: "700 15px/1.2 var(--ui, 'Hanken Grotesk', sans-serif)", color: "var(--ink, #241522)" }}>{groupTitle}</h3>
        <p style={{ margin: 0, font: "500 13px/1.4 var(--ui, 'Hanken Grotesk', sans-serif)", color: "var(--ink-2, #6b5a66)" }}>{groupSub}</p>
      </div>
      <div className="relgroup-body">{children}</div>
    </div>
  );
}

/** „abgeleitet" hinter einer von KMVS abgeleiteten Beziehung (B20). */
function AbgeleitetMarke({ rel }: { rel: Rel }) {
  if (!rel.abgeleitet) return null;
  return (
    <span className="rel-abgeleitet" title="Von KMVS aus einer anderen Beziehung abgeleitet">
      abgeleitet
    </span>
  );
}

function RelSection({
  title,
  sub,
  columns,
  rows,
  rowSelected,
  onAdd,
  onRowClick,
  anchorId,
  headerExtra,
}: {
  title: string;
  sub: string;
  columns: Column<Rel>[];
  rows: Rel[];
  rowSelected?: (r: Rel) => boolean;
  onAdd?: () => void;
  onRowClick?: (r: Rel) => void;
  /** Sprungziel für die Status-Pille (siehe ANCHOR_*). */
  anchorId?: string;
  /** Zusätzliche Kopf-Aktion(en) links neben „Hinzufügen" (z. B. Organigramm). */
  headerExtra?: React.ReactNode;
}) {
  // Bearbeiten/Entfernen laufen jetzt pro Zeile über die Aktions-Spalte (Pen +
  // Trash) — keine Checkbox-Auswahl / untere „Bearbeiten"-Leiste mehr.
  return (
    <section className="relsec" id={anchorId}>
      <div className="relsec-h relsec-h-row">
        <div>
          <h3>{title}</h3>
          <p>{sub}</p>
        </div>
        {(onAdd || headerExtra) && (
          <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
            {headerExtra}
            {onAdd && (
              <button type="button" className="rel-add" onClick={onAdd}>
                <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Hinzufügen
              </button>
            )}
          </div>
        )}
      </div>
      <DataTable columns={columns} rows={rows} rowKey={(_, i) => i} rowSelected={rowSelected} onRowClick={onRowClick ? (r) => onRowClick(r) : undefined} emptyText="Keine Einträge" />
    </section>
  );
}

function KvSection({ group }: { group?: Group }) {
  const showKv = !!(group?.type === "Kundenverbindung" && group?.id && group?.name);
  return (
    <section className="relsec">
      <div className="relsec-h">
        <h3>Kundenverbindungen</h3>
        <p>Sammelmappe — gruppiert alle zusammengehörigen Kontakte</p>
      </div>
      {showKv ? (
        <KundenverbindungBox group={{ id: group!.id!, name: group!.name! }} />
      ) : (
        <div className="kv-list">
          <div className="kv-grp">
            <div className="kv-grp-head" style={{ cursor: "default", color: "var(--ink-3)" }}>
              Keiner Kundenverbindung zugeordnet
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function ContactConnections({
  contactId,
  relations,
  relationsDest,
  group,
  contactType,
  self,
  canWrite,
  onChanged,
  jumpTo,
}: {
  contactId?: number;
  relations?: RelEntry[];
  relationsDest?: RelEntry[];
  group?: Group;
  contactType: string;
  self?: SelfContact;
  canWrite?: boolean;
  onChanged?: () => void;
  /** Sprungwunsch aus der Status-Pille. `nonce` löst denselben Sprung erneut aus. */
  jumpTo?: { anchorId: string; nonce: number } | null;
}) {
  const [open, setOpen] = React.useState(true);
  /**
   * Die Verbindungen zu anderen Unternehmen sind bei einer Firma ein eigener
   * Abschnitt mit eigenem Klapp-Zustand.
   *
   * Vorher steckten sie unten im selben Block wie Geschäftsführung,
   * Gesellschafter und Zuständigkeiten. Das sind aber zwei verschiedene
   * Fragen: „Wer führt und hält dieses Unternehmen?" und „Woran ist es
   * beteiligt?" — die erste stellt die Sachbearbeitung täglich, die zweite
   * beim Aufbau des Verbunds. Wer nur eine davon braucht, klappt die andere
   * jetzt weg.
   */
  const [offenVerbindungen, setOffenVerbindungen] = React.useState(true);
  // Sprung aus der Status-Pille: der Block ist zuklappbar, also erst öffnen und
  // das Aufklappen rendern lassen, dann scrollen (sonst zielt scrollIntoView auf
  // ein noch zusammengeklapptes Element).
  React.useEffect(() => {
    if (!jumpTo?.anchorId) return;
    // Beide Blöcke öffnen: Welcher den Anker trägt, hängt am Kontakttyp — und
    // ein Sprung in einen zugeklappten Block zielt ins Leere.
    setOpen(true);
    setOffenVerbindungen(true);
    const id = window.requestAnimationFrame(() => {
      const el = elementMitId(jumpTo.anchorId);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("jump-flash");
      window.setTimeout(() => el.classList.remove("jump-flash"), 1500);
    });
    return () => window.cancelAnimationFrame(id);
  }, [jumpTo?.anchorId, jumpTo?.nonce]);

  const [dialog, setDialog] = React.useState<RelKind | null>(null);
  const [editState, setEditState] = React.useState<{ kind: RelKind; edit: EditRelation } | null>(null);
  const [menschDialog, setMenschDialog] = React.useState(false);
  const [orgChart, setOrgChart] = React.useState(false);
  // Verantwortlichkeit-Popup (Add/Edit) für gebündelte Zeilen: Firma-Verantwortlich-
  // keiten (company-Perspektive) bzw. Person→Firma (person-Perspektive).
  const [verantPopup, setVerantPopup] = React.useState<{
    perspective: "company" | "person";
    mode: "add" | "edit";
    companyId?: number;
    /** Rechtsform der Firma — sie entscheidet, welche Organrollen wählbar sind. */
    companyLegalFormId?: number;
    fixedMensch?: ContactRef;
    lockedRef?: ContactRef;
    initialVerant?: VerantwortlichkeitValue;
    /** Benennung im Dialog: „Firma" oder „Verein/Gemeinschaft". */
    kontext?: "firma" | "vereinGemeinschaft";
  } | null>(null);
  const navigate = useNavigate();
  const isCompany = contactType === "Firma";
  const editable = !!(canWrite && contactId);
  // Benennung nach der eigenen Rechtsform: ein e.V. führt eine „Verantwortung für
  // den Verein". Nur die Firma-Sicht — in der Person-Sicht listet dieselbe
  // Sektion Firmen verschiedener Rechtsformen, eine Überschrift kann dort keiner
  // davon folgen (Plan 6.5).
  const begriff = useUnternehmensBegriff(isCompany ? self?.legalFormId : undefined);
  // … und derselbe Katalog für die GEGENkontakte: In der Person-Sicht entscheidet
  // die Rechtsform der jeweiligen Zeile, ob sie zu den Firmen oder in den
  // Abschnitt „Vereine und Gemeinschaften" gehört.
  const begriffVon = useUnternehmensBegriffe();
  // Der aktuelle Kontakt als Ref (Mensch-Seite im person-Perspektive-Popup).
  const selfRef: ContactRef | undefined = contactId
    ? { contactId, type: isCompany ? "Firma" : "Person", displayName: self?.name }
    : undefined;

  // Eine ausgewählte Zeile → Edit-Spezifikation für das RelationCreateDialog bauen.
  // Nur Zeilen mit eindeutiger relationId + Richtung sind editierbar.
  const openEdit = React.useCallback(
    (kind: RelKind) => (rel: Rel) => {
      if (rel.relationId == null || rel.srcId == null || rel.destId == null || rel.id == null) return;
      setEditState({
        kind,
        edit: {
          relationId: rel.relationId,
          srcId: rel.srcId,
          destId: rel.destId,
          other: { id: rel.id, name: rel.name, type: rel.otherType || "Person", gender: rel.otherGender },
          relType: rel.relType,
          rawRelType: rel.storedRelType,
          reverseRelType: rel.reverseRelationType,
          function: rel.func,
          share: rel.share,
          isExtern: rel.isExtern,
          sameHousehold: rel.sameHousehold,
        },
      });
    },
    [],
  );

  // Klick auf eine verknüpfte Person/Firma → deren Detailseite (nicht bei Self/ohne ID).
  const handleRowClick = React.useCallback(
    (rel: Rel) => {
      if (rel.id && rel.id > 0 && !rel.isSelf) navigate(`/contacts/${rel.id}`);
    },
    [navigate],
  );

  // „Mensch im Unternehmen"-Verbindungen werden in den Verknüpfungslisten NICHT
  // angezeigt — sie haben ihre eigene, eingeklappte Mitarbeiter-Tabelle
  // (MenschenImUnternehmenSection, aus den ROHEN relations/relationsDest gespeist).
  const rels = React.useMemo(
    () =>
      // Dieselbe Beteiligung steht oft zweimal in den Daten (Tochterfirma der
      // Mutter + Gesellschafter der Tochter) — in der Akte ist sie EINE
      // Verbindung (User 28.09.2026, data/anteilsbeziehung.ts).
      ohneAnteilsDubletten(
        toRels(relations ?? [], relationsDest ?? [], contactId).filter((r) => r.relType !== "Mensch im Unternehmen"),
      ),
    [relations, relationsDest, contactId],
  );

  // Wer ist hier wessen private Assistenz? Eigener Abruf, weil die Zuordnung
  // eine Beziehungsebene TIEFER liegt als alles, was die Akte sonst lädt
  // (Firma → Leitungsperson → Assistenz). Nur Id-Paare — die Namen stehen
  // schon in `rels`. Scheitert der Abruf, fällt lediglich der Zusatz „von …"
  // weg; die Assistenz selbst steht weiterhin in der Tabelle.
  const [assistenzPaare, setAssistenzPaare] = React.useState<AssistenzPaar[]>([]);
  React.useEffect(() => {
    if (!isCompany || !contactId) {
      setAssistenzPaare([]);
      return;
    }
    let abgebrochen = false;
    apiFetch<AssistenzPaar[]>(`contact-relation/assistenzen/${contactId}`)
      .then(({ data }) => {
        if (!abgebrochen) setAssistenzPaare(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!abgebrochen) setAssistenzPaare([]);
      });
    return () => {
      abgebrochen = true;
    };
  }, [isCompany, contactId]);

  /** Kontakt-id der Assistenz → Name der Person, der sie zuarbeitet. */
  const chefNamen = React.useMemo(
    () =>
      assistenzChefNamen(
        assistenzPaare,
        new Map(rels.flatMap((r) => (r.id ? [[r.id, r.name] as const] : []))),
      ),
    [assistenzPaare, rels],
  );

  // Der Rollen-Katalog trägt die Kategorie, nach der einsortiert wird (Organ/
  // Beteiligung vs. reine Zuständigkeit). Modulweit gecacht, für jeden
  // angemeldeten Nutzer lesbar.
  const { items: rollenKatalog, loading: katalogLaedt } = useVerantwortlichkeiten();
  const kategorieIndex = React.useMemo(() => baueKategorieIndex(rollenKatalog), [rollenKatalog]);
  // Noch nicht geladen ODER Request gescheitert (der Hook schluckt den Fehler und
  // liefert eine leere Liste): dann gilt die BISHERIGE Regel. Sonst sortierte die
  // Akte nach einem Fehlschlag dauerhaft still falsch. Ein leerer Katalog ist nie
  // legitim — findAll liefert auch inaktive Zeilen.
  const katalogUnsicher = katalogLaedt || rollenKatalog.length === 0;

  const removeRel = React.useCallback(
    async (rel: Rel) => {
      if (!contactId) return;
      try {
        if (rel.relationId != null) {
          // Chirurgisch nur DIESE eine Zeile entfernen — löst im Backend die orphan-
          // Schwester-Bereinigung aus, falls es eine 'Tochterfirma'-Kante war (SP5).
          // B21: Wurden aus ihr weitere Beziehungen abgeleitet, wird gefragt, ob
          // sie mit wegsollen.
          const zusatz = await loeschZusatzFuerAbgeleitete(rel.relationId);
          await apiFetch(`contact-relation/${rel.relationId}${zusatz}`, { method: "DELETE" });
        } else if (rel.id) {
          // Gebündelte Anzeige (mehrere Rows je Person, keine eindeutige relationId)
          // → wie bisher alle Relationen zwischen dem Paar entfernen.
          await apiFetch(`contact-relation/between/${contactId}/${rel.id}`, { method: "DELETE" });
        }
        onChanged?.();
      } catch {
        /* ignore */
      }
    },
    [contactId, onChanged],
  );
  // Edit der gebündelten Verantwortlichkeiten (Firma-Seite): company-Perspektive,
  // feste Firma = aktueller Kontakt, Gegenkontakt (Person) fix, Rollen vorbefüllt.
  /**
   * Alle Rollen-Zeilen des Paars — nicht nur die der angeklickten Sektion.
   *
   * Seit die Einsortierung der Rolle folgt, steht dieselbe Person in zwei
   * Tabellen, wenn sie Geschäftsführerin ist UND eine Zuständigkeit trägt.
   * `attachMenschen(items, true)` ersetzt beim Speichern aber ALLE verwalteten
   * Zeilen des Paars — bekäme das Popup nur `r.groupRels` seiner Sektion, fiele
   * die jeweils andere Rolle weg. Struktur-Kanten bleiben draußen: Sie werden vom
   * Ersetzen nicht erfasst und kämen sonst als Freitext-Zuständigkeit zurück.
   */
  const rollenDesPaars = React.useCallback(
    (r: Rel, perspektive: "company" | "person"): Rel[] =>
      rels.filter((x) => {
        if (x.id !== r.id) return false;
        if (!VERWALTETE_ROLLEN_TYPEN.has((x.storedRelType ?? x.relType).trim())) return false;
        // Person↔Firma: Die Firma ist eindeutig die Firmen-Seite, egal wie herum
        // die Zeile gespeichert ist — die App schreibt Firma→Person, der DEV-Seed
        // Person→Firma. Hier NICHT nach Richtung filtern: sonst öffnete das Popup
        // leer, und attachMenschen(items, true) löscht beim Speichern
        // richtungsunabhängig ALLE verwalteten Zeilen des Paars.
        if (!isCompany || x.otherType !== "Firma") return true;
        // Firma↔Firma: Hier sagt die Richtung, WER die Rolle trägt.
        //   company-Perspektive → der andere trägt sie HIER     → ausgehend
        //   person-Perspektive  → dieser Kontakt trägt sie DORT → eingehend
        return perspektive === "person" ? !!x.istEingehend : !x.istEingehend;
      }),
    [rels, isCompany],
  );

  /**
   * Die einzeln adressierbare Struktur-Kante einer (ggf. gebündelten) Zeile.
   *
   * `groupVerant` leert relationId/srcId/destId am Sammelobjekt — `openEdit`
   * steigt ohne sie per Guard aus und der Stift täte still nichts. Deshalb wird
   * die Kante aus den Teilzeilen geholt.
   */
  const strukturKanteVon = React.useCallback(
    (r: Rel): Rel | null =>
      (r.groupRels?.length ? r.groupRels : [r]).find(
        (row) =>
          istStrukturKante(row) &&
          row.relationId != null &&
          row.srcId != null &&
          row.destId != null,
      ) ?? null,
    [],
  );

  const openVerantCompany = React.useCallback(
    (r: Rel) => {
      if (!r.id) return;
      setVerantPopup({
        perspective: "company",
        mode: "edit",
        companyId: contactId,
        // Firma-Sicht: die Rechtsform des aktuellen Kontakts ist maßgeblich.
        companyLegalFormId: self?.legalFormId,
        lockedRef: { contactId: r.id, type: (r.otherType as "Person" | "Firma") || "Person", displayName: r.name },
        initialVerant: relsToVerant(rollenDesPaars(r, "company")),
      });
    },
    [contactId, self?.legalFormId, rollenDesPaars],
  );
  // Edit der gebündelten Verbindungen zu Firmen (Person-Seite): person-Perspektive,
  // Mensch = aktueller Kontakt, Firma (= r.id) fix, Rollen vorbefüllt.
  const openVerantPerson = React.useCallback(
    (r: Rel) => {
      if (!r.id) return;
      setVerantPopup({
        perspective: "person",
        mode: "edit",
        companyId: r.id,
        // Person-Sicht: maßgeblich ist die Rechtsform der Gegen-Firma (mkRel).
        companyLegalFormId: r.otherLegalFormId,
        fixedMensch: selfRef,
        lockedRef: { contactId: r.id, type: "Firma", displayName: r.name },
        initialVerant: relsToVerant(rollenDesPaars(r, "person")),
        // Der Dialog beschriftet sich nach dem, was der Gegenkontakt ist —
        // „Verein oder Gemeinschaft" statt „Firma", sobald seine Rechtsform ein
        // eigenes `verbindungLabel` trägt.
        kontext: istKeinUnternehmensBegriff(begriffVon(r.otherLegalFormId, r.otherLegalForm))
          ? "vereinGemeinschaft"
          : "firma",
      });
    },
    [selfRef, begriffVon, rollenDesPaars],
  );

  // Aktions-Spalte: Pen (Bearbeiten) + Trash (Entfernen) pro Zeile. Nicht für die
  // Self-Zeile / id-lose Zeilen. `onEdit` je Tabelle; ohne onEdit nur Löschen.
  const actionsCol = (onEdit?: (r: Rel) => void): Column<Rel> => ({
    id: "act",
    header: "",
    className: "colact",
    cell: (r) =>
      r.isSelf || !r.id ? null : (
        <span className="rel-actbar">
          {onEdit && (
            <button
              type="button"
              className="rel-edit"
              title="Bearbeiten"
              onClick={(e) => { e.stopPropagation(); onEdit(r); }}
            >
              {PenIcon}
            </button>
          )}
          <button
            type="button"
            className="rel-del"
            title="Entfernen"
            onClick={(e) => { e.stopPropagation(); removeRel(r); }}
          >
            {DelIcon}
          </button>
        </span>
      ),
  });

  // Firma-Sektionen (disjunkt): Organe und Beteiligte → Tab 1, Zuständigkeiten →
  // Tab 2, der Firmenverbund → Tab 3. Entscheidend ist die ROLLE, nicht der
  // Kontakttyp des Gegenübers: Eine Steuerkanzlei mit der Zuständigkeit „Steuern"
  // ist eine Zuständigkeit und keine Beteiligung; eine Kommanditistin oder
  // Komplementär-GmbH gehört umgekehrt zu den Gesellschaftern. Vorher entschied
  // hier `otherType === "Firma"` allein (PO 20.08.2026).
  //
  // Eine EINGEHENDE Firma↔Firma-Rollenkante bleibt bei den Verbindungen: Dort
  // trägt DIESE Firma die Rolle bei einer anderen (attach-menschen schreibt immer
  // `src = Unternehmen`) — das ist eine Beteiligung bzw. ein Mandat dieses
  // Unternehmens, keine Verantwortlichkeit FÜR dieses Unternehmen.
  //
  // Die Regel selbst steht in rollenEinordnung.ts, wo sie ohne React prüfbar ist.
  // `isGfG` bleibt der Rückfall für die Handelsregister-Freitexte, die in keinem
  // Katalog stehen („Vorstand", „Liquidator" …).
  const sektion = (r: Rel) =>
    sektionFuerZeile(r, kategorieIndex, { katalogUnsicher, istFreitextOrgan: isGfG(r) });

  // Geschäftsführer und Gesellschafter zuerst, darunter die übrigen Organe
  // (Vorstand, Prokuristen …), je alphabetisch.
  const tabGfg = groupVerant(rels.filter((r) => sektion(r) === "organ")).sort(
    (a, b) =>
      Number(istKernGfG(b)) - Number(istKernGfG(a)) ||
      (a.name ?? "").localeCompare(b.name ?? "", "de"),
  );
  // Eine Zeile je Gegenüber, alle Zuständigkeiten gebündelt — Personen UND Firmen
  // (die Typ-Spalte der Tabelle unterscheidet sie bereits).
  const tabVerant = rels.filter((r) => sektion(r) === "zustaendigkeit");
  // „Private Assistenz" → „Private Assistenz von ‹Name›". Erst NACH dem Bündeln:
  // `groupVerant` fasst je Person eine Zeile zusammen, und beschriftet wird der
  // fertige Chip — nicht die Rohzeile, die auch anderswo gelesen wird.
  const tabVerantGrouped = groupVerant(tabVerant).map((r) =>
    r.id && chefNamen.has(r.id)
      ? { ...r, items: chipsMitAssistenzName(r.items ?? [], chefNamen.get(r.id)) }
      : r,
  );
  // Eine Zeile je Gegenueber: zu derselben Firma existieren regelmaessig MEHRERE
  // Kanten (Struktur + Rolle), seit `ensureGesellschafterAusMutter` zu jeder
  // 'Tochterfirma' zusaetzlich eine 'Gesellschafter'-Kante anlegt. Die Firma gibt
  // es aber nur einmal (PO 08.09.2026). Die Regel steht in
  // firmenverbindungenBuendeln.ts, wo sie ohne React pruefbar ist — und bewusst
  // NICHT in `groupVerant`: das leert relationId/srcId/destId (Stift stiege still
  // aus) und baut die Labels ohne Richtung.
  const tabFirmaC = buendleFirmenverbindungen(
    rels.filter((r) => sektion(r) === "firmenverbindung"),
  ).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "de"));

  // Person-Kategorien: Personen-Beziehungen (nicht Firma) → Haushalt vs. Weitere;
  // Firmen → Verbindungen zu Firmen.
  const personRels = rels.filter((r) => r.otherType !== "Firma");
  const household = personRels.filter((r) => r.sameHousehold);
  const weitere = personRels.filter((r) => !r.sameHousehold);
  // Firmen-Verbindungen der Person, getrennt nach dem, was der Gegenkontakt IST:
  // Verein wie Eigentümergemeinschaft sind datenmodellhaft eine Firma, in der
  // Akte aber keine — weder die Mitgliedschaft im Schützenverein noch der
  // Miteigentumsanteil an der ETG gehört unter „Geschäftsbeziehungen".
  // Die Unterscheidung kommt aus `verbindungLabel` (Stammdatum der Rechtsform),
  // nicht aus einer zweiten Liste von Rechtsformen. Mengenmäßig führen dabei die
  // Gemeinschaften (PROD: 52 ETG/GdWE, 41 Verein, 4 Stiftung) — die Benennung des
  // Abschnitts richtet sich deshalb nach beidem, nicht nur nach dem Verein.
  const alleFirmenRels = rels.filter((r) => r.otherType === "Firma");
  const istVereinGemeinschaftsRel = (r: Rel) =>
    istKeinUnternehmensBegriff(begriffVon(r.otherLegalFormId, r.otherLegalForm));
  const personFirmen = alleFirmenRels.filter((r) => !istVereinGemeinschaftsRel(r));
  const personVereine = alleFirmenRels.filter(istVereinGemeinschaftsRel);

  // Haushalt: Kontakt selbst als erste, hervorgehobene Zeile (nur wenn Mitglieder).
  const selfRow: Rel | null = self
    ? { id: self.id, name: self.name, relType: "", isExtern: false, sameHousehold: true, otherType: self.typ || "Person", status: self.status, isSelf: true }
    : null;
  const householdRows = household.length > 0 && selfRow ? [selfRow, ...household] : household;

  // Spalten
  const gfgCols: Column<Rel>[] = [
    { id: "name", header: "Name", className: "name", cell: (r) => r.name },
    { id: "rolle", header: "Rolle", cell: (r) => gfgRolle(r) || <Dash /> },
    { id: "anteile", header: "Anteile", cell: (r) => <AnteileZelle rel={r} /> },
    { id: "komm", header: "Kontaktmöglichkeit", cell: (r) => <KomCell rel={r} /> },
    { id: "status", header: "K-Status", cell: (r) => <StatusChip s={r.status} /> },
  ];
  const verantCols: Column<Rel>[] = [
    { id: "typ", header: "Typ", cell: (r) => contactTypeLabel(r.otherType || "Person") },
    { id: "name", header: "Name", className: "name", cell: (r) => r.name },
    { id: "verant", header: "Verantwortlichkeit", cell: (r) => <VerantList items={r.items} /> },
    { id: "komm", header: "Kontaktmöglichkeit", cell: (r) => <KomCell rel={r} /> },
    { id: "status", header: "Kontaktstatus", cell: (r) => <StatusChip s={r.status} /> },
  ];
  const firmaCColsC: Column<Rel>[] = [
    { id: "name", header: "Name", className: "name", cell: (r) => r.name },
    {
      id: "rolle",
      header: "Rolle",
      // Alle Rollen des Gegenübers in EINER Zelle, Struktur-Kante zuerst:
      // „Tochterfirma · dort Gesellschafter". Struktur-Kanten tragen ihr Label im
      // Typ (bei eingehenden Zeilen bereits gedreht); Rollenkanten haben keine
      // Typ-Inverse und sagen deshalb die Richtung, wie das Organigramm es seit
      // jeher tut (rollenRichtung.ts).
      cell: (r) => firmenverbindungRollenText(r) || <Dash />,
    },
    { id: "anteile", header: "Anteile", cell: (r) => <AnteileZelle rel={r} /> },
    { id: "komm", header: "Kontaktmöglichkeit", cell: (r) => <KomCell rel={r} /> },
    { id: "status", header: "Kontaktstatus", cell: (r) => <StatusChip s={r.status} /> },
  ];
  // Richtung verdeutlichen: „<Beziehung> von <aktueller Kontakt>" (z. B. „Tochter von Max").
  const selfName = self?.name?.trim();
  const withSelf = (label: string) => (selfName ? `${label} von ${selfName}` : label);
  // Person: Haushalt + Weitere Beziehungen
  const householdCols: Column<Rel>[] = [
    { id: "name", header: "Name", className: "name", cell: (r) => (r.isSelf ? <strong>{r.name}</strong> : r.name) },
    { id: "rolle", header: "Rolle / Beziehung", cell: (r) => (r.isSelf ? "Dieser Kontakt" : r.relType ? <>{withSelf(r.relType)}<AbgeleitetMarke rel={r} /></> : <Dash />) },
    { id: "komm", header: "Kontaktmöglichkeit", cell: (r) => <KomCell rel={r} /> },
    { id: "status", header: "Kontaktstatus", cell: (r) => <StatusChip s={r.status} /> },
  ];
  const weitereCols: Column<Rel>[] = [
    { id: "name", header: "Name", className: "name", cell: (r) => r.name },
    { id: "bez", header: "Beziehung", cell: (r) => { const b = r.relType || r.func; return b ? <>{withSelf(b)}<AbgeleitetMarke rel={r} /></> : <Dash />; } },
    { id: "komm", header: "Kontaktmöglichkeit", cell: (r) => <KomCell rel={r} /> },
    { id: "status", header: "Kontaktstatus", cell: (r) => <StatusChip s={r.status} /> },
  ];
  // Person: Verbindungen zu Firmen — je Firma gebündelt, alle Verantwortlichkeiten
  // zusammengefasst (eine Zeile/Firma, Chips statt Einzelstring).
  const personFirmaGrouped = groupVerant(personFirmen);
  const personFirmaCols: Column<Rel>[] = [
    { id: "name", header: "Name", className: "name", cell: (r) => r.name },
    { id: "verant", header: "Verantwortlichkeit", cell: (r) => <VerantList items={r.items} /> },
    { id: "komm", header: "Kontaktmöglichkeit", cell: (r) => <KomCell rel={r} /> },
    { id: "status", header: "Kontaktstatus", cell: (r) => <StatusChip s={r.status} /> },
  ];
  // Person: Vereine und Gemeinschaften — dieselbe Bündelung, aber die Spalte
  // heißt „Rolle": Hier trägt man ein Amt, ist Mitglied oder hält einen
  // Miteigentumsanteil — keine „Verantwortlichkeit fürs Unternehmen".
  const personVereinGrouped = groupVerant(personVereine);
  const personVereinCols: Column<Rel>[] = [
    { id: "name", header: "Name", className: "name", cell: (r) => r.name },
    { id: "rolle", header: "Rolle", cell: (r) => <VerantList items={r.items} /> },
    { id: "komm", header: "Kontaktmöglichkeit", cell: (r) => <KomCell rel={r} /> },
    { id: "status", header: "Kontaktstatus", cell: (r) => <StatusChip s={r.status} /> },
  ];

  if (editable) {
    // Pen je Tabelle: einzeln-adressierbare Zeilen → RelationCreateDialog; gebündelte
    // Verantwortlichkeiten → Verantwortlichkeit-Popup (alle Rollen des Paars).
    // Gebündelt (eine Zeile je Person) → keine eindeutige relationId mehr, also
    // wie bei den Verantwortlichkeiten über das Popup mit allen Rollen des Paars
    // bearbeiten statt über RelationCreateDialog.
    // Gesellschafter-Strukturkanten (Mutter, ausgehende Beteiligung) haben kein
    // Gegenstück im Verantwortlichkeits-Popup: Das verwaltet nur
    // `Mensch fürs Unternehmen`, `Geschäftsführer` und `Gesellschafter`
    // (VERWALTETE_ROLLEN_TYPEN). Trägt das Paar KEINE solche Rolle, käme dort
    // nichts an und der Anwender stünde vor einem leeren Popup — dann gehört die
    // Struktur-Kante in den Firmen-Verbindungs-Dialog. Gibt es dagegen zusätzlich
    // eine echte Gesellschafter-Zeile (der Regelfall, seit
    // ensureGesellschafterAusMutter sie anlegt), gewinnt das Popup: dort steht
    // die Quote, und die Struktur-Kante fasst es nicht an.
    gfgCols.push(
      actionsCol((r) => {
        const struktur = strukturKanteVon(r);
        if (struktur && rollenDesPaars(r, "company").length === 0) {
          openEdit("firmaConn")(struktur);
          return;
        }
        openVerantCompany(r);
      }),
    );
    verantCols.push(actionsCol(openVerantCompany));
    // Struktur-Kanten des Verbunds → Firmen-Verbindungs-Dialog. Rollenkanten
    // gehören ins Verantwortlichkeits-Popup: Dort ist dieser Kontakt der
    // Rolleninhaber (person-Perspektive). RelationCreateDialog(kind="firmaConn")
    // kennt nur die vier Struktur-Typen, kippt über canonicalFirmaConn die
    // Richtung und legt die Zeile ohne `function` neu an — Öffnen und Speichern
    // zerstörte hier Richtung UND Zuständigkeit.
    // Gebuendelte Zeile → die Aktion adressiert die LEITZEILE (Struktur-Kante
    // bevorzugt), nicht das Sammelobjekt: `istStrukturKante` am Sammelobjekt haenge
    // sonst davon ab, welche Kante zufaellig als Kopf der Gruppe landete.
    firmaCColsC.push(
      actionsCol((r) => {
        const leit = leitzeileFuerAktion(r) as Rel;
        return istStrukturKante(leit) ? openEdit("firmaConn")(leit) : openVerantPerson(leit);
      }),
    );
    householdCols.push(actionsCol(openEdit("household")));
    weitereCols.push(actionsCol(openEdit("weitere")));
    personFirmaCols.push(actionsCol(openVerantPerson));
    personVereinCols.push(actionsCol(openVerantPerson));
  }

  const chev = (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M6 3.5L10.5 8 6 12.5" /></svg>
  );
  const linkIco = (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M8 11a3 3 0 004.4.3l2.3-2.3a3 3 0 10-4.2-4.2l-1.3 1.3" /><path d="M12 9a3 3 0 00-4.4-.3L5.3 11a3 3 0 104.2 4.2l1.3-1.3" /></svg>
  );
  const orgIco = (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="4.4" r="2.2" /><circle cx="4.5" cy="15.2" r="2.2" /><circle cx="15.5" cy="15.2" r="2.2" /><path d="M10 6.6v2.9M10 9.5l-4.3 3.7M10 9.5l4.3 3.7" /></svg>
  );
  // Organigramm-Button: Ansehen geht immer (deshalb unabhängig von canWrite
  // sichtbar); das Bearbeiten im Popup ist über dessen canWrite-Prop gegatet.
  const orgButton = contactId ? (
    <button type="button" className="rel-add" title="Organigramm des Unternehmensverbunds anzeigen" onClick={() => setOrgChart(true)}>
      {orgIco} Organigramm
    </button>
  ) : undefined;

  return (
    <>
    <div className={"rel-wrap" + (open ? "" : " collapsed")}>
      <div className="rel-head" onClick={() => setOpen((o) => !o)}>
        <button className="fsec-toggle" aria-label={open ? "Einklappen" : "Ausklappen"}>{chev}</button>
        <span className="fsec-ico">{linkIco}</span>
        {/* Bei einer Firma trägt dieser Block nur noch die Verantwortung —
            die Verbindungen stehen darunter in einem eigenen Abschnitt. */}
        <h2>{isCompany ? verantwortungTitel(begriff) : "Beziehungen und Verbindungen"}</h2>
      </div>
      <div className="rel-body">
        {isCompany ? (
          <>
            <RelGroup groupTitle={verantwortungTitel(begriff)} groupSub="Geschäftsführung, Gesellschafter und Ansprechpartner">
              <RelSection title="Geschäftsführer und Gesellschafter" sub={`Personen und Firmen, die ${diesesUnternehmen(begriff)} führen oder daran beteiligt sind`} columns={gfgCols} rows={tabGfg} onAdd={editable ? () => setMenschDialog(true) : undefined} onRowClick={handleRowClick} />
              <RelSection anchorId={ANCHOR_VERBINDUNGEN} title="Verantwortlichkeiten und Zuständigkeiten" sub={`Personen und Firmen mit einer Zuständigkeit für ${diesesUnternehmen(begriff)}`} columns={verantCols} rows={tabVerantGrouped} onAdd={editable ? () => setMenschDialog(true) : undefined} onRowClick={handleRowClick} />
            </RelGroup>
            {/* Die eigene Rechtsform entscheidet über die Beschriftung: Ein
                Verein hat Mitglieder, keine Belegschaft — und dort ist
                zusätzlich wichtig, ob wir das Mitglied selbst kennen. */}
            <MenschenImUnternehmenSection
              companyId={contactId}
              relations={relations}
              relationsDest={relationsDest}
              canWrite={editable}
              istVerein={istVereinsRechtsform(self?.legalForm)}
            />
          </>
        ) : (
          <>
            <RelGroup groupTitle="Beziehungen" groupSub="Familie, Haushalt und Bekannte">
              <RelSection anchorId={ANCHOR_HAUSHALT} title="Haushalt" sub="Personen, die mit diesem Kontakt im selben Haushalt leben" columns={householdCols} rows={householdRows} rowSelected={(r) => !!r.isSelf} onAdd={editable ? () => setDialog("household") : undefined} onRowClick={handleRowClick} />
              <RelSection title="Weitere Beziehungen" sub="Verbundene Personen außerhalb des Haushalts" columns={weitereCols} rows={weitere} onAdd={editable ? () => setDialog("weitere") : undefined} onRowClick={handleRowClick} />
              {contactId ? (
                <BeziehungsVorschlaege contactId={contactId} editable={editable} reloadKey={relations} onChanged={onChanged} />
              ) : null}
            </RelGroup>
            <RelGroup groupTitle="Verbindungen" groupSub="Unternehmen, Vereine und Gemeinschaften">
              <RelSection anchorId={ANCHOR_VERBINDUNGEN} title="Verbindungen zu Firmen" sub="Firmen, für die dieser Kontakt verantwortlich oder an denen er beteiligt ist" columns={personFirmaCols} rows={personFirmaGrouped} onAdd={editable ? () => setVerantPopup({ perspective: "person", mode: "add", fixedMensch: selfRef, kontext: "firma" }) : undefined} onRowClick={handleRowClick} />
              {/* Eigener Abschnitt statt einer erweiterten Firmen-Überschrift: Weder
                  die Vereinsmitgliedschaft noch der Miteigentumsanteil an der ETG
                  ist eine Geschäftsbeziehung, und in EINER Tabelle verschwänden sie
                  zwischen Beteiligungen und Zuständigkeiten. Der Untertitel nennt
                  Amt, Mitgliedschaft UND Anteil, weil die Rollenmatrix für GdWE/ETG
                  genau das anbietet (Verwalter, Wohnungs-/Miteigentümer mit
                  Bruchteilsquote). Was hierher gehört, sagt `verbindungLabel`. */}
              <RelSection title="Vereine und Gemeinschaften" sub="Vereine, Stiftungen, Genossenschaften und Eigentümergemeinschaften — mit Amt, Mitgliedschaft oder Miteigentumsanteil" columns={personVereinCols} rows={personVereinGrouped} onAdd={editable ? () => setVerantPopup({ perspective: "person", mode: "add", fixedMensch: selfRef, kontext: "vereinGemeinschaft" }) : undefined} onRowClick={handleRowClick} />
              <KvSection group={group} />
            </RelGroup>
          </>
        )}
      </div>
      {dialog && contactId && (
        <RelationCreateDialog
          contactId={contactId}
          kind={dialog}
          selfName={self?.name}
          selfGender={self?.gender}
          selfSalutation={self?.salutationLabel}
          onClose={() => setDialog(null)}
          onSaved={() => onChanged?.()}
        />
      )}
      {editState && contactId && (
        <RelationCreateDialog
          contactId={contactId}
          kind={editState.kind}
          selfName={self?.name}
          selfGender={self?.gender}
          selfSalutation={self?.salutationLabel}
          editRelation={editState.edit}
          onClose={() => setEditState(null)}
          onSaved={() => onChanged?.()}
        />
      )}
      {menschDialog && contactId && (
        <MenschenFuersUnternehmenDialog
          companyId={contactId}
          companyLegalFormId={self?.legalFormId}
          onClose={() => setMenschDialog(false)}
          onSaved={() => onChanged?.()}
        />
      )}
      {verantPopup && contactId && (
        <MenschenFuersUnternehmenDialog
          perspective={verantPopup.perspective}
          mode={verantPopup.mode}
          companyId={verantPopup.companyId}
          // Kommt aus dem Popup-State, weil die Firma je nach Perspektive eine
          // andere ist: in der Firma-Sicht der aktuelle Kontakt, in der
          // Person-Sicht der Gegenkontakt der Zeile. Beide Fälle brauchen die
          // Rechtsform — für die Rollenfilterung wie für die Benennung.
          companyLegalFormId={verantPopup.companyLegalFormId}
          kontext={verantPopup.kontext}
          fixedMensch={verantPopup.fixedMensch}
          lockedRef={verantPopup.lockedRef}
          initialVerant={verantPopup.initialVerant}
          onClose={() => setVerantPopup(null)}
          onSaved={() => { onChanged?.(); setVerantPopup(null); }}
        />
      )}
      {orgChart && contactId && (
        <OrganigrammPopup
          companyId={contactId}
          companyName={self?.name}
          canWrite={editable}
          onChanged={onChanged}
          onClose={() => setOrgChart(false)}
        />
      )}
    </div>

    {/* Eigener Abschnitt, eigener Klapp-Zustand: „Woran ist dieses Unternehmen
        beteiligt, und wer hält es?" ist eine andere Frage als die nach
        Geschäftsführung und Zuständigkeiten darüber. Die Dialoge bleiben oben —
        sie sind Overlays und gehören in keinen zuklappbaren Block; die Knöpfe
        hier unten schalten dieselben Zustände. */}
    {isCompany && (
      <div className={"rel-wrap" + (offenVerbindungen ? "" : " collapsed")}>
        <div className="rel-head" onClick={() => setOffenVerbindungen((o) => !o)}>
          <button className="fsec-toggle" aria-label={offenVerbindungen ? "Einklappen" : "Ausklappen"}>{chev}</button>
          <span className="fsec-ico">{linkIco}</span>
          <h2>Verbindungen</h2>
        </div>
        <div className="rel-body">
          <RelGroup groupTitle="Verbindungen" groupSub="Geschäftsbeziehungen zu anderen Unternehmen">
            <RelSection title="Verbindungen zu Firmen" sub={`Tochterunternehmen und Beteiligungen — und Rollen, die ${diesesUnternehmen(begriff, "nominativ")} bei anderen Firmen trägt`} columns={firmaCColsC} rows={tabFirmaC} onAdd={editable ? () => setDialog("firmaConn") : undefined} onRowClick={handleRowClick} headerExtra={orgButton} />
            <KvSection group={group} />
          </RelGroup>
        </div>
      </div>
    )}
    </>
  );
}
